import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "utils/cn";

interface PageTitleBarProps {
  /** Page title — use the same i18n nav label as the sidebar entry so the two never drift. Omit on pages whose hero already shows the title. */
  title?: string;
  /** Merged onto the outer bar (e.g. to drop the bottom border where a page supplies its own). */
  className?: string;
  /** Optional controls rendered inline after the title (left-aligned). Keep the far-right clear for the floating DashboardTopBar. */
  children?: React.ReactNode;
  /** Where the page sits, root first ("My Assets", "Design Assets"): drawn as a trail in place of `title`. */
  trail?: string[];
}

// Compact 48px page header shared across every left-menu page. Sits at the very top of the
// content area and is height-aligned with the sidebar logo row (both h-12) so they read as one
// row. The left side carries the optional page title — or, on a page with sections, its trail
// ("My Assets › Design Assets"); the sidebar's collapse toggle lives in the sidebar itself, beside
// the logo. The global Academy / Agent / avatar cluster (DashboardTopBar) floats over the right side, so anything added via `children` must stay
// on the left. On pages that also have a bigger hero (Brand DNAs, Moodboards, …) the title is
// omitted and this bar stays as an empty band that still hosts the floating cluster.
const PageTitleBar: React.FC<PageTitleBarProps> = ({ title, className, children, trail }) => {
  // shrink-0 is load-bearing: several pages place this inside a `flex flex-col h-screen` column,
  // where flexbox would otherwise compress the h-12 bar once the content below grows past the
  // viewport (the "correct for a moment, then collapses" bug on Explore Trends).
  return (
    <div className={cn("flex shrink-0 items-center h-12 border-b border-black-300", className)}>
      {trail?.length ? (
        <nav aria-label={trail[trail.length - 1]} className="flex min-w-0 items-center gap-1.5 px-3.5 text-head6">
          {trail.map((crumb, index) => {
            const last = index === trail.length - 1;

            return (
              <React.Fragment key={crumb}>
                {index > 0 && <ChevronRight size={16} className="shrink-0 text-grey" aria-hidden />}
                <span className={cn("truncate", last ? "text-trueBlack dark:text-white" : "text-grey")} {...(last ? { "aria-current": "page" as const } : {})}>
                  {crumb}
                </span>
              </React.Fragment>
            );
          })}
        </nav>
      ) : (
        title && <h2 className="px-3.5 text-head6 text-trueBlack dark:text-white">{title}</h2>
      )}
      {children}
    </div>
  );
};

export default PageTitleBar;
